import { Box, Stack } from '@mui/material';
import type { ReactNode } from 'react';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { layout } from '@/theme';

/**
 * The empty state of a page section: a tinted icon badge, a heading, one line
 * of explanation, and the ways in.
 *
 * Shared rather than copied because Planning's and Financials' had already
 * drifted — 52px badge against 48, icon 26 against 24 — while claiming to be
 * the same pattern. The numbers here are Planning's, which is the one the
 * design review signed off.
 *
 * It belongs in this deliberately small collection on the same test as the
 * others: there is nothing in MUI to stand on, and two callers with identical
 * structure are exactly the drift a primitive prevents.
 *
 * Sits INSIDE a section's bordered card, not in place of one — the card and the
 * section header above it belong to the caller (see `PlanPanel`).
 */
export type EmptyStateProps = {
  /** Material Symbol name for the badge. */
  icon: string;
  title: string;
  body: string;
  /** The actions row: primary first. */
  children?: ReactNode;
  automationId?: string;
};

export const EmptyState = ({ icon, title, body, children, automationId }: EmptyStateProps) => (
  <Box
    data-automation-id={automationId}
    sx={{
      px: 3,
      // 48px is the top of the design system's spacing scale.
      pt: 6,
      pb: 6,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    }}
  >
    <Box
      data-automation-id="empty-state-badge"
      sx={{
        width: 52,
        height: 52,
        borderRadius: 'var(--border-radius-pill)',
        bgcolor: 'var(--color-pink-10)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
      }}
    >
      <MaterialSymbol name={icon} size={26} color="var(--color-pink-80)" />
    </Box>
    <Box
      data-automation-id="empty-state-title"
      sx={{ fontSize: 16, fontWeight: 500, color: 'var(--fg-1)', mb: 1 }}
    >
      {title}
    </Box>
    <Box
      data-automation-id="empty-state-body"
      sx={{
        fontSize: 14,
        color: 'var(--fg-3)',
        maxWidth: layout.proseMaxWidth,
        lineHeight: 1.5,
        mb: 2.5,
      }}
    >
      {body}
    </Box>
    {children ? (
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {children}
      </Stack>
    ) : null}
  </Box>
);
